import { MDXRemote, MDXRemoteSerializeResult } from "next-mdx-remote";
import { Container } from "react-grid-system";
import Accelerator from "@components/accelerators/Accelerator";
import GitHubLogo from "@components/accelerators/GitHubLogo";
import HacksterLogo from "@components/accelerators/HacksterLogo";
import Breadcrumbs from "@components/Breadcrumbs";
import SmartLink from "@components/doc/SmartLink";
import Sidebar from "@components/layout/Sidebar";
import { MDXComponentMap } from "@components/MDXComponentMap";
import Tags from "@components/Tags";
import { ImgSrc } from "@config/image";
import theme from "@config/theme";
import { stringToURI } from "@scripts/utils";

interface AcceleratorsProjectPageProps {
  accelerator: Accelerator;
  readmeSource: MDXRemoteSerializeResult;
  readmeTOC: MDXRemoteSerializeResult;
}
const AcceleratorsProjectPage = ({
  accelerator,
  readmeSource,
  readmeTOC,
}: AcceleratorsProjectPageProps) => (
  <Container>
    <Breadcrumbs
      links={[
        { name: "Accelerators", url: "/accelerators" },
        {
          name: accelerator.name,
          url: `/community/projects/${stringToURI(accelerator.name)}`,
        },
      ]}
    />

    <h1>
      <img
        width="40"
        height="40"
        alt=""
        src={ImgSrc(
          `/images/accelerators/app-icons/${accelerator.id}-icon.svg`,
        )}
      />
      <span>{accelerator.name}</span>
    </h1>

    <Tags
      tags={accelerator.tags.map((tag) => ({
        name: tag,
      }))}
    />

    <div className="content-section">
      <div className="content doc">
        {/* @ts-expect-error Server Component */}
        <MDXRemote {...readmeSource} components={MDXComponentMap} />
      </div>
      <Sidebar>
        <>
          <h3>On This Page</h3>
          {/* @ts-expect-error Server Component */}
          <MDXRemote {...readmeTOC} components={MDXComponentMap} />

          <h3>Resources</h3>
          <ul>
            {accelerator.github ? (
              <li className="item-with-icon">
                <SmartLink href={accelerator.github}>
                  <span>
                    <GitHubLogo />
                    GitHub
                  </span>
                </SmartLink>
              </li>
            ) : null}
            {accelerator.hackster ? (
              <li className="item-with-icon">
                <SmartLink href={accelerator.hackster}>
                  <span>
                    <HacksterLogo />
                    Hackster
                  </span>
                </SmartLink>
              </li>
            ) : null}
            <li>
              <SmartLink
                href={`https://blues.com/landing-pages/accelerators-contact-us/?accelerator=${encodeURIComponent(
                  accelerator.name,
                )}`}
              >
                Request More Info
              </SmartLink>
            </li>
          </ul>
        </>
      </Sidebar>
    </div>

    <style jsx>
      {`
        h1 {
          display: flex;
          gap: 1rem;
          margin-top: 0;
          margin-bottom: 1rem;
          align-items: center;
        }
        :global(.tags) {
          margin: 0 0 1rem 0;
        }

        .content-section {
          display: flex;
          border-top: 1px solid ${theme.colors.gray4};
          gap: 2rem;
        }
        .content {
          overflow: hidden;
        }
        .content :global(img) {
          max-width: 100%;
        }
        .content :global(li) {
          margin: 0.5rem 0;
        }
        .content :global(li .doc-image) {
          padding-top: 1rem;
        }

        :global(.sidebar) span {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
        }
        .item-with-icon :global(svg) {
          margin-right: 5px;
          height: 30px;
        }

        @media only screen and (max-width: ${theme.breakpoints.md}px) {
          .content-section {
            flex-direction: column-reverse;
          }
          .content-section :global(.sidebar) {
            position: initial;
            overflow: initial;
            height: auto;
            width: auto;
            padding-bottom: 0;
            padding-bottom: 1rem;
            border-bottom: 1px solid ${theme.colors.gray4};
          }
        }
      `}
    </style>
  </Container>
);

export default AcceleratorsProjectPage;
